import React, { useEffect, useMemo, useRef, useState } from 'react';
import DatamapsIndia from 'react-datamaps-india';
import { fetchAllDataAPI } from '../../utils/api';

const GeoChart = () => {
    const wrapRef = useRef(null);

    const [regionData, setRegionData] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    // wrapper size (for fallback)
    const [box, setBox] = useState({ w: 0, h: 0 });

    // ✅ actual map svg box inside wrapper (this fixes arrow misalignment)
    const [mapBox, setMapBox] = useState({ left: 0, top: 0, w: 0, h: 0 });

    // ✅ arrow color (you can change)
    const ARROW = {
        stroke: '#0b7a3b',
        dot: '#0b7a3b',
        head: '#0b7a3b',
    };

    // ✅ unique colors per state (loop)
    const stateColors = useMemo(
        () => [
            '#2E7D32', '#1565C0', '#6A1B9A', '#EF6C00', '#C62828',
            '#00838F', '#4E342E', '#283593', '#AD1457', '#455A64',
            '#1B5E20', '#0D47A1', '#4A148C', '#E65100', '#B71C1C',
            '#006064', '#3E2723', '#1A237E', '#880E4F', '#263238',
        ],
        []
    );

    // ✅ positions in % (0–100) relative to MAP SVG (not wrapper)
    const calloutPositions = useMemo(
        () => ({
            'Tamil Nadu': { x: 76, y: 80, ax: 56, ay: 74, bend: 14 },
            'Karnataka': { x: 66, y: 72, ax: 46, ay: 64, bend: -10 },
            'Kerala': { x: 66, y: 88, ax: 46, ay: 86, bend: 12 },

            'Maharashtra': { x: 56, y: 60, ax: 42, ay: 52, bend: -10 },
            'Madhya Pradesh': { x: 52, y: 46, ax: 34, ay: 36, bend: -10 },
            'Rajasthan': { x: 40, y: 32, ax: 24, ay: 24, bend: -10 },

            'Uttar Pradesh': { x: 58, y: 34, ax: 78, ay: 26, bend: -14 },

            'Telangana': { x: 66, y: 62, ax: 84, ay: 56, bend: -10 },
            'Andhra Pradesh': { x: 72, y: 68, ax: 88, ay: 62, bend: -10 },
            'West Bengal': { x: 76, y: 40, ax: 92, ay: 34, bend: -10 },

            'Gujarat': { x: 34, y: 48, ax: 18, ay: 38, bend: -10 },
            'Delhi': { x: 54, y: 24, ax: 70, ay: 16, bend: -10 },
        }),
        []
    );

    useEffect(() => {
        fetchLocationData();
    }, []);

    // ✅ measure wrapper size + actual map svg box (very important)
    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;

        const update = () => {
            // wrapper size
            const r = el.getBoundingClientRect();
            setBox({ w: r.width, h: r.height });

            // find map svg inside DatamapsIndia
            const svg = el.querySelector('svg');
            if (!svg) return;

            const svgRect = svg.getBoundingClientRect();
            setMapBox({
                left: svgRect.left - r.left,
                top: svgRect.top - r.top,
                w: svgRect.width,
                h: svgRect.height,
            });
        };

        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        window.addEventListener('resize', update);

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', update);
        };
    }, [isLoading, regionData]);

    const fetchLocationData = async () => {
        setIsLoading(true);
        try {
            const res = await fetchAllDataAPI('super-admin-get-all-data-location-wise-chart');
            if (res?.data?.status === 1) {
                const transformedData = transformRegionData(res.data.locationData);
                setRegionData(transformedData);
            }
        } catch (error) {
            console.error('Error fetching chart data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const transformRegionData = (data) => {
        const stateNameMapping = {
            "ANDHRA PRADESH": "Andhra Pradesh",
            "ARUNACHAL PRADESH": "Arunachal Pradesh",
            "ASSAM": "Assam",
            "BIHAR": "Bihar",
            "CHHATTISGARH": "Chhattisgarh",
            "GOA": "Goa",
            "GUJARAT": "Gujarat",
            "HARYANA": "Haryana",
            "HIMACHAL PRADESH": "Himachal Pradesh",
            "JAMMU AND KASHMIR": "Jammu & Kashmir",
            "JHARKHAND": "Jharkhand",
            "KARNATAKA": "Karnataka",
            "KERALA": "Kerala",
            "MADHYA PRADESH": "Madhya Pradesh",
            "MAHARASHTRA": "Maharashtra",
            "MANIPUR": "Manipur",
            "MEGHALAYA": "Meghalaya",
            "MIZORAM": "Mizoram",
            "NAGALAND": "Nagaland",
            "ODISHA": "Odisha",
            "PUNJAB": "Punjab",
            "RAJASTHAN": "Rajasthan",
            "SIKKIM": "Sikkim",
            "TAMIL NADU": "Tamil Nadu",
            "TELANGANA": "Telangana",
            "TRIPURA": "Tripura",
            "UTTAR PRADESH": "Uttar Pradesh",
            "UTTARAKHAND": "Uttarakhand",
            "WEST BENGAL": "West Bengal",
            "ANDAMAN AND NICOBAR ISLANDS": "Andaman & Nicobar Islands",
            "CHANDIGARH": "Chandigarh",
            "DADRA AND NAGAR HAVELI AND DAMAN AND DIU": "Dadra and Nagar Haveli and Daman and Diu",
            "LAKSHADWEEP": "Lakshadweep",
            "DELHI": "Delhi",
            "PUDUCHERRY": "Puducherry",
            "LADAKH": "Ladakh"
        };

        const formattedData = {};
        let i = 0;

        (data || []).forEach((item) => {
            const stateName = stateNameMapping[item.order_state] || item.order_state;
            const val = parseInt(item.total, 10);

            if (!formattedData[stateName]) {
                formattedData[stateName] = {
                    name: stateName,
                    value: Number.isFinite(val) ? val : 0,
                    fill: stateColors[i % stateColors.length], // ✅ different color per state
                };
                i++;
            }
        });

        return formattedData;
    };

    const HoverComponent = React.memo(({ value }) => {
        if (!value || !value.name) return <div>No Data Available</div>;
        return (
            <div>
                <div><b>State:</b> {value.name}</div>
                <div><b>Total Orders:</b> {value.value}</div>
            </div>
        );
    });

    // ✅ Top N
    const TOP_N = 8;

    const topCallouts = useMemo(() => {
        const arr = Object.values(regionData || {})
            .map((x) => ({ name: x.name, value: Number(x.value) || 0 }))
            .sort((a, b) => b.value - a.value);

        return arr
            .filter((x) => calloutPositions[x.name])
            .slice(0, TOP_N)
            .map((x) => ({ ...x, ...calloutPositions[x.name] }));
    }, [regionData, calloutPositions]);

    // convert % -> px (using MAP svg box)
    const px = (p, total) => (p / 100) * total;


    return (
        <div
            className="geochartsection"
            ref={wrapRef}
            style={{ position: 'relative' }}
        >
            {isLoading ? (
                <p>Loading...</p>
            ) : (
                <>
                    <DatamapsIndia
                        regionData={regionData}
                        hoverComponent={({ value }) => <HoverComponent value={value} />}
                        mapLayout={{
                            title: "",
                            legendTitle: "Order Count",
                            noDataColor: "#f5f5f5",
                            borderColor: "#dddddd",
                            hoverBorderColor: "#333333",
                            hoverColor: "#2b8a3e",
                        }}
                    />

                    {/* ✅ overlay locked to actual MAP SVG area */}
                    {mapBox.w > 50 && mapBox.h > 50 && topCallouts.length > 0 && (
                        <svg
                            style={{
                                position: 'absolute',
                                left: mapBox.left,
                                top: mapBox.top,
                                width: mapBox.w,
                                height: mapBox.h,
                                pointerEvents: 'none',
                            }}
                            viewBox={`0 0 ${mapBox.w} ${mapBox.h}`}
                            preserveAspectRatio="none"
                        >
                            <defs>
                                <marker
                                    id="arrowHead"
                                    markerWidth="10"
                                    markerHeight="10"
                                    refX="9"
                                    refY="5"
                                    orient="auto"
                                >
                                    <path d="M0,0 L10,5 L0,10 Z" fill={ARROW.head} />
                                </marker>
                            </defs>

                            {topCallouts.map((c) => {
                                const x1 = px(c.x, mapBox.w);
                                const y1 = px(c.y, mapBox.h);
                                const x2 = px(c.ax, mapBox.w);
                                const y2 = px(c.ay, mapBox.h);

                                const mx = (x1 + x2) / 2;
                                const my = (y1 + y2) / 2;
                                const bend = c.bend ?? -20;
                                const cx = mx;
                                const cy = my + bend;

                                const d = `M ${x1} ${y1} Q ${cx} ${cy} ${x2} ${y2}`;

                                const labelText = `${c.name} (${c.value})`;

                                const estW = Math.min(260, Math.max(140, labelText.length * 7 + 28));
                                const estH = 38;

                                // clamp X/Y so box stays inside map
                                const pad = 8;
                                const safeX = Math.min(Math.max(x2 - 10, pad), mapBox.w - estW - pad);
                                const safeY = Math.min(Math.max(y2 - 18, pad), mapBox.h - estH - pad);

                                return (
                                    <g key={c.name}>
                                        <circle cx={x1} cy={y1} r="4" fill={ARROW.dot} />

                                        <path
                                            d={d}
                                            fill="none"
                                            stroke={ARROW.stroke}
                                            strokeWidth="3"
                                            markerEnd="url(#arrowHead)"
                                            vectorEffect="non-scaling-stroke"
                                            strokeLinecap="round"
                                        />

                                        <foreignObject x={safeX} y={safeY} width={estW} height={estH}>
                                            <div
                                                style={{
                                                    background: 'rgba(255,255,255,0.96)',
                                                    border: '1px solid rgba(0,0,0,0.12)',
                                                    borderRadius: '8px',
                                                    padding: '6px 10px',
                                                    fontSize: '13px',
                                                    color: '#111',
                                                    display: 'block',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                                    width: '100%',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                                title={labelText} // hover pe full text
                                            >
                                                {labelText}
                                            </div>
                                        </foreignObject>
                                    </g>
                                );
                            })}
                        </svg>
                    )}
                </>
            )}
        </div>
    );
};

export default GeoChart;
